<template>
    <div>
      <Header />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Additional Earnings</h5>
          <p class="page-sub">Bonuses and extra pay added to payroll periods</p>
        </div>
        <div class="page-actions">
  
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search earnings..." @input="tableData.page = 1; fetchData()" v-model="tableData.search" />
          </div>
  
          <div class="dropdown">
            <button class="btn-outline dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <font-awesome-icon icon="download" /> Export
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><a class="dropdown-item" href="#" @click="drawPdf(print_content, headerArrayColumn, 'p', 'payroll_additional_earning', 'Payroll Additional Earning')">
                <font-awesome-icon icon="file-pdf" class="me-2 text-danger" /> PDF
              </a></li>
              <li><a class="dropdown-item" href="#" @click="reusableMixinToCSV('payroll_additional_earning.csv')">
                <font-awesome-icon icon="file-csv" class="me-2 text-success" /> CSV
              </a></li>
            </ul>
          </div>
  
          <button class="btn-primary-action" :disabled="permission.process_payroll != 1" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> New Earning
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading earnings...</p>
        </div>
  
        <div v-else-if="permission_status == 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view this resource.</p>
        </div>
  
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--green"><font-awesome-icon icon="plus-circle" /></div>
          <p class="state-title">No additional earnings</p>
          <p class="state-sub">Add bonuses or extra pay to employee payslips.</p>
          <button v-if="permission.process_payroll == 1" class="btn-primary-action mt-3" @click="openCreateModal">
            <font-awesome-icon icon="plus" /> New Earning
          </button>
        </div>
  
        <template v-else>
          <div class="table-toolbar">
            <span class="table-count">Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong></span>
            <div class="toolbar-right">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option><option value="10">10</option><option value="20">20</option>
                <option value="30">30</option><option value="50">50</option><option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Period</th>
                  <th>Employee</th>
                  <th class="text-right">Amount</th>
                  <th>Description</th>
                  <th>Taxable</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in details" :key="index">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <p class="period-name">{{ row?.period?.name }}</p>
                    <span class="batch-code">{{ row?.period?.reference }}</span>
                  </td>
  
                  <td>
                    <div class="employee-cell">
                      <div class="employee-avatar">{{ initials(row.employee) }}</div>
                      <span class="employee-name">{{ row?.employee?.first_name }} {{ row?.employee?.last_name }}</span>
                    </div>
                  </td>
  
                  <td class="text-right fw-bold earning-value">{{ formatPrice(row.amount) }}</td>
  
                  <td class="text-muted-cell">{{ row.description || '—' }}</td>
  
                  <td>
                    <span class="taxable-badge" :class="row.is_taxable ? 'taxable-badge--yes' : 'taxable-badge--no'">
                      <font-awesome-icon :icon="row.is_taxable ? 'check' : 'times'" class="me-1" />
                      {{ row.is_taxable ? 'Taxable' : 'Non-taxable' }}
                    </span>
                  </td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <button class="action-btn action-btn--edit"
                        :disabled="permission.process_payroll != 1"
                        @click="permission.process_payroll == 1 ? openEditModal(index) : null"
                        title="Edit">
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button class="action-btn action-btn--delete"
                        :disabled="permission.process_payroll != 1"
                        @click="getOneModal(index)"
                        :data-bs-toggle="permission.process_payroll == 1 ? 'modal' : ''"
                        data-bs-target="#singleModal"
                        title="Delete">
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination :pagination="pagination" @prev="tableData.page = pagination.currentPage - 1; fetchData()" @next="tableData.page = pagination.currentPage + 1; fetchData()" />
          </div>
        </template>
      </div>
  
  
      <!-- ── Create Modal ── -->
      <div class="modal fade" id="create_earning" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(22,163,74,.1);color:#16a34a;">
                <font-awesome-icon icon="plus-circle" />
              </div>
              <div>
                <h6 class="modal-clean-title">New Additional Earning</h6>
                <p class="modal-clean-sub">Add a bonus or extra pay to an employee</p>
              </div>
              <button type="button" class="btn-close ms-auto" data-bs-dismiss="modal"></button>
            </div>
            <form @submit.prevent="createEarning">
              <div class="modal-body">
                <div class="modal-fields">
  
                  <div class="field-group">
                    <label class="field-label">Payroll Period <span class="req">*</span></label>
                    <select v-model="earning.payroll_period_id" class="field-input field-select">
                      <option value="" disabled>— Select period —</option>
                      <option v-for="p in periods" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Employee <span class="req">*</span></label>
                    <select v-model="earning.employee_id" class="field-input field-select">
                      <option value="" disabled>— Select employee —</option>
                      <option v-for="e in employees" :key="e.id" :value="e.id">
                        {{ e.first_name }} {{ e.last_name }} — {{ e.employee_no }}
                      </option>
                    </select>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Amount</label>
                    <input type="text" class="field-input" placeholder="0.00" v-model="earning.amount"
                      onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Description</label>
                    <input type="text" class="field-input" placeholder="e.g. Performance bonus" v-model="earning.description" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Taxable <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="earning.is_taxable">
                      <option :value="true">Yes — Taxable</option>
                      <option :value="false">No — Non-taxable</option>
                    </select>
                  </div>
  
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" data-bs-dismiss="modal">Cancel</button>
                <button type="submit" class="btn-success-action" :disabled="crudLoading">
                  <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  <font-awesome-icon v-else icon="plus" class="me-2" />
                  {{ crudLoading ? 'Creating...' : 'Create Earning' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Update Modal ── -->
      <div class="modal fade" id="update_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(0,74,173,.1);color:#004AAD;">
                <font-awesome-icon icon="pen" />
              </div>
              <div>
                <h6 class="modal-clean-title">Update Earning</h6>
                <p class="modal-clean-sub">{{ currentData?.employee?.first_name }} {{ currentData?.employee?.last_name }}</p>
              </div>
              <button type="button" class="btn-close ms-auto" @click="closeUpdateModal"></button>
            </div>
            <form @submit.prevent="updateEarning">
              <div class="modal-body">
                <div class="modal-fields">
  
                  <div class="field-group">
                    <label class="field-label">Payroll Period <span class="req">*</span></label>
                    <select v-model="currentData.payroll_period_id" class="field-input field-select">
                      <option v-for="p in periods" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Employee <span class="req">*</span></label>
                    <select v-model="currentData.employee_id" class="field-input field-select">
                      <option v-for="e in employees" :key="e.id" :value="e.id">
                        {{ e.first_name }} {{ e.last_name }} — {{ e.employee_no }}
                      </option>
                    </select>
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Amount</label>
                    <input type="text" class="field-input" placeholder="0.00" v-model="currentData.amount"
                      onkeypress="if(isNaN(this.value+String.fromCharCode(event.keyCode)))return false;" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Description</label>
                    <input type="text" class="field-input" placeholder="e.g. Performance bonus" v-model="currentData.description" />
                  </div>
  
                  <div class="field-group">
                    <label class="field-label">Taxable <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="currentData.is_taxable">
                      <option :value="1">Yes — Taxable</option>
                      <option :value="0">No — Non-taxable</option>
                    </select>
                  </div>
  
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" @click="closeUpdateModal">Cancel</button>
                <button type="submit" class="btn-primary-action" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  {{ loading ? 'Saving...' : 'Update Earning' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="deleteEarning"
        :modalText="'Are you sure you want to delete this additional earning?'" :action="'deleteSingle'" />
  
    </div>
  </template>
  
  <script>
  import { reusableMixin } from '../../../../lib/reusableMixin';
  import Header from './component/header.vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useStore } from 'vuex';
  import SingleDeleteModal from '../../../../components/ActionModal.vue';
  
  export default {
    components: { Header, SingleDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      return {
        print_content:    [],
        headerArrayColumn: ['S/N','Period Name','Period ID','Employee','Amount','Description','Taxable'],
        details:          [],
        loading:          false,
        crudLoading:      false,
        currentData:      {},
        periods:          [],
        employees:        [],
        permission_status: null,
  
        earning: { payroll_period_id:'', employee_id:'', amount:0, description:'', is_taxable:false },
  
        tableData: { page: 1, draw:0, length:10, search:'', column:0, dir:'desc', id:'' },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast:  useToast(),
        store:  useStore(),
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    created() {
      Promise.all([ this.fetchData(), this.fetchPeriods(), this.fetchEmployees() ]);
    },
  
    methods: {
      initials(employee) {
        if (!employee) return '?';
        return ((employee.first_name?.[0] ?? '') + (employee.last_name?.[0] ?? '')).toUpperCase();
      },
  
      dataToPrint(details) {
        this.print_content = details.map((d, idx) => ({
          sn:          idx + 1,
          name:        d.period?.name,
          reference:   d.period?.reference,
          employee:    `${d.employee?.first_name} ${d.employee?.last_name}`,
          amount:      d.amount,
          description: d.description,
          taxable:     d.is_taxable ? 'Yes' : 'No',
        }));
      },
  
      fetchData(url = '/payroll/additional-earning') {
        this.loading = true;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.details = response.data.data.data;
            this.dataToPrint(this.details);
            this.configPagination(response.data.data);
            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            if (error?.response?.status == 403) { this.permission_status = 403; this.toast.error(error.response.data.message); }
          });
      },
  
      fetchPeriods() {
        return axios.get('/payroll/period/open', { params: { paginate: 0 } })
          .then(response => { this.periods = response?.data?.data ?? []; })
          .catch(() => {});
      },
  
      fetchEmployees() {
        return axios.get('/payroll/employees/active', { params: { paginate: 0 } })
          .then(response => { this.employees = response?.data?.data ?? []; })
          .catch(() => {});
      },
  
      openCreateModal()  { window.$('#create_earning').modal('show'); },
      closeUpdateModal() { window.$('#update_modal').modal('hide'); },
  
      async createEarning() {
        this.crudLoading = true;
        axios.post('payroll/additional-earning', this.earning)
          .then(response => {
            this.crudLoading = false;
            if (response.data.status === 200) {
              this.toast.success(response.data.message);
              Object.assign(this.earning, { payroll_period_id:'', employee_id:'', amount:0, description:'', is_taxable:false });
              this.fetchData();
              window.$('#create_earning').modal('hide');
            } else { this.toast.error(response.data.message); }
          })
          .catch(error => { this.crudLoading = false; this.toast.error(error.response?.data?.message ?? 'Something went wrong'); });
      },
  
      async updateEarning() {
        this.loading = true;
        axios.put(`/payroll/additional-earning/${this.currentData.id}`, this.currentData)
          .then(() => {
            this.loading = false;
            this.toast.success('Earning updated successfully');
            this.fetchData(); this.closeUpdateModal();
          })
          .catch(error => {
            this.loading = false;
            this.toast.error(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      deleteEarning() {
        axios.delete(`/payroll/additional-earning/${this.currentData.id}`)
          .then(() => { this.toast.success('Earning deleted successfully'); this.fetchData(); })
          .catch(error => { this.toast.error(error.response?.data?.message ?? 'Could not delete earning'); });
      },
  
      getOneModal(index)   { this.currentData = this.details[index]; },
      openEditModal(index) { this.currentData = { ...this.details[index] }; window.$('#update_modal').modal('show'); },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:200px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-success-action { display:inline-flex; align-items:center; gap:7px; background:#16a34a; color:#fff; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-success-action:hover:not(:disabled) { background:#15803d; }
  .btn-success-action:disabled { opacity:.5; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  .me-1 { margin-right:4px; } .me-2 { margin-right:8px; } .mt-3 { margin-top:12px; } .ms-auto { margin-left:auto; }
  
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  .state-block  { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon   { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--green { background:rgba(22,163,74,.1); color:#16a34a; }
  .state-icon--red   { background:rgba(224,53,53,.1); color:#e03535; }
  .state-title  { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell { color:#4a5e78; }
  
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .toolbar-right { display:flex; align-items:center; gap:8px; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 14px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 14px; font-size:13px; vertical-align:middle; }
  .col-actions { width:80px; }
  .text-right  { text-align:right; }
  .fw-bold     { font-weight:700; }
  .earning-value { color:#16a34a; }
  
  .period-name  { font-size:13px; font-weight:600; color:#1a2b45; margin:0 0 4px; }
  .batch-code   { font-family:monospace; font-size:11.5px; font-weight:600; color:#7a90b0; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:5px; padding:1px 6px; }
  
  .employee-cell   { display:flex; align-items:center; gap:9px; }
  .employee-avatar { width:32px; height:32px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
  .employee-name   { font-size:13px; font-weight:600; color:#1a2b45; }
  
  .taxable-badge     { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; }
  .taxable-badge--yes { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
  .taxable-badge--no  { background:#f4f7fb; color:#7a90b0; border-color:#e4eaf2; }
  
  .action-btns  { display:flex; align-items:center; gap:5px; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; transition:background .15s; background:#fff; }
  .action-btn:disabled { opacity:.4; cursor:not-allowed; }
  .action-btn--edit   { color:#004AAD; } .action-btn--edit:hover:not(:disabled)   { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; } .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  .dropdown-menu { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:160px; }
  .dropdown-item { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Modals ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .modal-fields { display:flex; flex-direction:column; gap:13px; }
  .field-group  { display:flex; flex-direction:column; gap:5px; }
  .field-label  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req          { color:#e03535; }
  .field-input  { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-select { appearance:auto; cursor:pointer; }
  
  @media(max-width:991px) { .page-header,.page-actions { flex-direction:column; align-items:stretch; } .search-input { width:100%; } }
  </style>